import Icon from '@/components/ui/AppIcon';

interface DifferentiationItem {
  title: string;
  description: string;
  iconName: string;
}

interface DifferentiationSectionProps {
  className?: string;
}

const DifferentiationSection = ({ className = '' }: DifferentiationSectionProps) => {
  const differentiators: DifferentiationItem[] = [
    {
      title: "No Performance Guarantees",
      description: "We don't promise ROAS or conversion rates. We provide reliable infrastructure that gives you the tools to achieve your own results through professional execution.",
      iconName: "ShieldCheckIcon"
    },
    {
      title: "Compliance-First Architecture",
      description: "Every system decision prioritizes regulatory alignment. We operate in Hong Kong SAR specifically for jurisdictional advantages in global advertising compliance.",
      iconName: "DocumentCheckIcon"
    },
    {
      title: "Transparent Limitations",
      description: "We clearly state what we don't do: no creative services, no strategy consulting, no performance management. We are infrastructure, not an agency.",
      iconName: "ClipboardDocumentListIcon"
    },
    {
      title: "Prepaid Transparency",
      description: "No hidden fees, no percentage cuts, no performance-based pricing. You prepay for infrastructure access, we provide reliable service. Simple, institutional-grade billing.",
      iconName: "CurrencyDollarIcon"
    },
    {
      title: "White-Label Infrastructure",
      description: "Your campaigns run under our compliant accounts, but the strategy, creative, and optimization remain entirely yours. We're the backbone, not the brain.",
      iconName: "ServerStackIcon"
    },
    {
      title: "Professional-Only Service",
      description: "No beginner hand-holding, no educational content, no basic support. We serve agencies and professional advertisers who know what they're doing.",
      iconName: "UserGroupIcon"
    }
  ];

  return (
    <section className={`py-20 lg:py-28 bg-card ${className}`}>
      <div className="mx-auto px-6 lg:px-12 max-w-7xl">
        <div className="max-w-3xl mb-16">
          <h2 className="font-headline font-bold text-3xl lg:text-4xl text-primary mb-6">
            How We're Different
          </h2>
          <p className="font-body text-lg text-text-secondary leading-relaxed">
            Traditional agencies promise results. We provide infrastructure. This fundamental difference shapes everything we do, from our pricing model to our client relationships.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {differentiators.map((item, index) => (
            <div
              key={index}
              className="bg-surface p-8 rounded-sm border border-border hover:border-accent transition-colors duration-300"
            >
              <div className="flex items-start space-x-4 mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-accent/10 rounded-sm flex items-center justify-center">
                  <Icon
                    name={item.iconName as any}
                    size={24}
                    variant="outline"
                    className="text-accent"
                  />
                </div>
                <h3 className="font-headline font-semibold text-xl text-primary pt-2">
                  {item.title}
                </h3>
              </div>
              <p className="font-body text-base text-text-secondary leading-relaxed">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DifferentiationSection;